// src/pages/CourseListPage.jsx
import { useCallback, useEffect, useMemo, useState } from "react"
import { Layers3, Plus, Edit, Trash2, Users, ClipboardList, Search, BookOpen, LayoutGrid, Table as TableIcon, Loader, Library, Crown, ImagePlus } from "lucide-react"
import { motion } from "framer-motion"
import Drawer from "@/layout/Drawer"
import ConfirmModal from "@/widgets/ConfirmModal"
import CourseUsersModal from "@/widgets/CourseUsersModal"
import CourseTestsModal from "@/widgets/CourseTestsModal"
import { getCourses, saveCourse, deleteCourse } from "@/api/kuizy"
import { formatDateTime } from "@/settings/master"
import ViewSwitcher from "../../components/ui/ViewSwitcher"
import { API_BASE } from "../../settings/master";
import CourseContentModal from "@/widgets/CourseContentModal";

const emptyCourse = {
  id: "",
  code: "",
  name: "",
  description: "",
  image_url: "",      // จาก backend (เช่น path หรือ data url)
  image_file: null,   // File ที่ user เลือกใหม่
  image_preview: ""   // blob url สำหรับ preview หน้าเว็บ
}

export default function CourseListPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState("card")
  const [searchIn, setSI] = useState("")
  const [searchTxt, setST] = useState("")
  const [drawer, setDrawer] = useState(false)
  const [draft, setDraft] = useState(emptyCourse)
  const [saving, setSaving] = useState(false)
  const [delItem, setDelItem] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [usersModal, setUsersModal] = useState({ open: false, courseId: null, courseName: "" })
  const [testsModal, setTestsModal] = useState({ open: false, courseId: null, courseName: "" })
  const [contentsModal, setContentsModal] = useState({ open: false, courseId: null, courseName: "" })
  const [alert, setAlert] = useState({ open: false, msg: "" })

  const load = useCallback(() => {
    setLoading(true)
    getCourses({ search: searchTxt || undefined })
      .then(r => setRows(Array.isArray(r.data) ? r.data : r.data?.data ?? []))
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [searchTxt])
  useEffect(load, [load])

  const list = useMemo(() => rows, [rows])

  const save = async (e) => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    console.log('draft :', draft);

    try {
      const formData = new FormData()
      formData.append('id', draft.id || '')
      formData.append('code', draft.code)
      formData.append('name', draft.name)
      formData.append('description', draft.description || '')
      if (draft.image_file) {
        formData.append('image', draft.image_file)
      }
      // ส่ง formData แทน draft
      await saveCourse(formData)

      setDrawer(false)
      setDraft(emptyCourse)
      load() // โหลดข้อมูลใหม่

    } catch (error) {
      console.error(error)
      setAlert({ open: true, msg: "บันทึกไม่สำเร็จ" })
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = () => {
    if (deleting) return
    setDeleting(true)
    deleteCourse(delItem.id)
      .then(load)
      .catch(() => setAlert({ open: true, msg: "ไม่สามารถลบได้" }))
      .finally(() => {
        setDeleting(false)
        setDelItem(null)
      })
  }

  const srcImage = (image_url) => {
    if (!image_url) return null

    const isAbsolute = /^(blob:|data:|http)/.test(image_url)
    if (isAbsolute) return image_url

    return `${API_BASE}${image_url}`
  }

  const handleFile = e => {
    const file = e.target.files?.[0]
    if (!file) return

    const previewUrl = URL.createObjectURL(file)

    setDraft(r => ({
      ...r,
      image_file: file,       // ส่งไฟล์ต้นฉบับไป backend
      image_preview: previewUrl // ใช้ preview แสดงบนจอ
    }))
  }

  return (
    <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="flex flex-col gap-6 p-4 sm:p-6">
      <motion.header initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }} className="flex flex-wrap items-end gap-3 justify-between">


        <h1 onClick={load} className="flex items-center gap-2 text-lg font-semibold cursor-pointer">
          <Library size={20} /> จัดการหลักสูตร ({rows.length})
        </h1>


        <div className="flex flex-wrap gap-2">
          <ViewSwitcher view={view} setView={setView} />
          <button disabled={saving} onClick={() => { setDraft(emptyCourse); setDrawer(true) }} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
            {saving ? <Loader size={16} className="animate-spin" /> : <Plus size={16} />}
            เพิ่มหลักสูตร
          </button>
        </div>
      </motion.header>

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }} className="flex flex-wrap items-end gap-4 bg-white rounded-xl shadow-md p-4">
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <label className="flex items-center gap-1 text-sm">
            <Search size={16} /> ค้นหา
          </label>
          <div className="relative">
            <input value={searchIn} onChange={e => setSI(e.target.value)} onKeyDown={e => e.key === "Enter" && setST(searchIn)} className="w-full border border-gray-300 rounded-lg px-10 py-2 text-sm focus:ring-2 focus:ring-primary" placeholder="ค้นหาหลักสูตร" />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>
        <button onClick={() => setST(searchIn)} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
          <Search size={16} /> ค้นหา
        </button>
      </motion.div>

      {view === "card" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35, delay: 0.1 }} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <Loader className="animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <div className="col-span-full text-center text-gray-500 py-12">– ไม่มีข้อมูล –</div>
          ) : (
            list.map(c => (
              <motion.div
                key={c.id}
                whileHover={{ y: -4 }}
                className="relative bg-white rounded-2xl shadow-md p-6 flex flex-col gap-4 overflow-hidden"
              >
                {/* 🔔 Notification Badge */}


                <div className="-mx-6 -mt-6 rounded-t-2xl overflow-hidden bg-neutral-100">
                  <img
                    src={srcImage(c.logo_url)}
                    alt={c.name}
                    className="w-full h-48"
                  />
                </div>

                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    <p className="text-sm text-gray-500">{c.code}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setDraft(c); setDrawer(true) }} className="text-primary hover:text-primary/80">
                      <Edit size={18} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setUsersModal({ open: true, courseId: c.id, courseName: c.name })}
                    className="bg-primary/10 rounded-lg p-3 flex flex-col items-center hover:bg-neutral-200"
                  >
                    <Crown size={18} className="text-secondary" />
                    <span className="text-[10px] text-gray-600 mt-1">ผู้สอน</span>
                    <strong className="text-lg">{c.user_count || 0}</strong>
                  </button>

                  <button
                    onClick={() => setContentsModal({ open: true, courseId: c.id, courseName: c.name })}
                    className="relative bg-primary/10 rounded-lg p-3 flex flex-col items-center hover:bg-neutral-200"
                  >
                    {c.pending_content_approval_count > 0 && (
                      <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-semibold min-w-[18px] h-[18px] flex items-center justify-center px-1 rounded-full shadow">
                        {c.pending_content_approval_count}
                      </div>
                    )}
                    <BookOpen size={18} className="text-secondary" />
                    <span className="text-[10px] text-gray-600 mt-1">เนื้อหา</span>
                    <strong className="text-lg">{c.content_count || 0}</strong>
                  </button>

                  <button
                    onClick={() => setTestsModal({ open: true, courseId: c.id, courseName: c.name })}
                    className="relative bg-primary/10 rounded-lg p-3 flex flex-col items-center hover:bg-neutral-200"
                  >
                    {c.pending_approval_count > 0 && (
                      <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-semibold min-w-[18px] h-[18px] flex items-center justify-center px-1 rounded-full shadow">
                        {c.pending_approval_count}
                      </div>
                    )}

                    <ClipboardList size={18} className="text-secondary" />
                    <span className="text-[10px] text-gray-600 mt-1">ชุดข้อสอบ</span>
                    <strong className="text-lg">{c.test_count || 0}</strong>
                  </button>
                </div>

                <div className="flex items-end justify-between">
                  <p className="text-xs text-gray-500">
                    <span>สร้างเมื่อ {formatDateTime(c.created_at)}</span>
                    <br />
                    <span>แก้ไขล่าสุด {formatDateTime(c.updated_at || c.created_at)}</span>
                  </p>
                  <button onClick={() => setDelItem(c)} className="text-red-600 hover:text-red-500">
                    <Trash2 size={18} />
                  </button>
                </div>
              </motion.div>
            ))

          )}
        </motion.div>
      )}

      {view === "table" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35, delay: 0.1 }} className="bg-white rounded-xl shadow-md overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-[880px] w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
              <tr>
                <th className="py-2 px-3 w-12 text-left">#</th>
                <th className="py-2 px-3 text-left">ชื่อย่อหลักสูตร</th>
                <th className="py-2 px-3 text-left">ชื่อหลักสูตร</th>
                <th className="py-2 px-3 text-center">ผู้สอน</th>
                <th className="py-2 px-3 text-center">เนื้อหา</th>
                <th className="py-2 px-3 text-center">ชุดข้อสอบ</th>
                <th className="py-2 px-3 text-right hidden md:table-cell">สร้างเมื่อ</th>
                <th className="py-2 px-3 text-right hidden md:table-cell">แก้ไขล่าสุด</th>
                <th className="py-2 px-3 w-44 text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center">
                    <Loader className="animate-spin" />
                  </td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-500">
                    – ไม่มีข้อมูล –
                  </td>
                </tr>
              ) : (
                list.map((c, i) => (
                  <tr key={c.id} className="odd:bg-white even:bg-gray-50 whitespace-nowrap">
                    <td className="py-2 px-3">{i + 1}</td>
                    <td className="py-2 px-3">{c.code}</td>
                    <td className="py-2 px-3">{c.name}</td>
                    <td className="py-2 px-3 text-center">
                      <button onClick={() => setUsersModal({ open: true, courseId: c.id, courseName: c.name })} className="text-primary font-bold hover:underline">
                        {c.user_count || 0}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button onClick={() => setContentsModal({ open: true, courseId: c.id, courseName: c.name })} className="relative text-primary font-bold hover:underline">
                        {c.content_count || 0}
                        {c.pending_content_approval_count > 0 && (
                          <span className="absolute -top-2 -right-3 bg-red-500 text-white text-[9px] px-1 rounded-full">{c.pending_content_approval_count}</span>
                        )}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button onClick={() => setTestsModal({ open: true, courseId: c.id, courseName: c.name })} className="text-primary font-bold hover:underline">
                        {c.test_count || 0}
                      </button>
                    </td>
                    {/* <td className="py-2 px-3 text-center">
                      {c.pending_approval_count > 0 ? (
                        <span className="inline-block bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">{c.pending_approval_count}</span>
                      ) : (
                        <span className="inline-block text-green-600 text-xs px-2 py-1 rounded-full">0</span>
                      )}
                    </td> */}
                    <td className="py-2 px-3 text-right hidden md:table-cell">{formatDateTime(c.created_at)}</td>
                    <td className="py-2 px-3 text-right hidden md:table-cell">{formatDateTime(c.updated_at)}</td>
                    <td className="py-2 px-3 text-right space-x-1">
                      <button onClick={() => { setDraft(c); setDrawer(true) }} className="text-primary hover:text-primary/80">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => setDelItem(c)} className="text-red-600 hover:text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </motion.div>
      )}

      <Drawer open={drawer} title={draft.id ? "แก้ไขหลักสูตร" : "เพิ่มหลักสูตร"} onClose={() => setDrawer(false)}>
        <form onSubmit={save} className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3">
            <label className="w-[370px] h-[200px] border border-gray-300 flex items-center justify-center relative overflow-hidden cursor-pointer hover:border-primary group bg-gray-50 rounded-lg">

              {draft.image_preview || draft.logo_url ? (
                <img
                  src={draft.image_preview || srcImage(draft.logo_url)}
                  alt="logo"
                  className="object-cover w-full h-full"
                />
              ) : (
                <ImagePlus className="text-gray-400 group-hover:text-primary transition-colors" size={36} />
              )}

              {/* Input File */}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFile}
              />

              {/* Optional: Overlay ตอนเอาเมาส์ชี้เพื่อให้รู้ว่าเปลี่ยนรูปได้ */}
              {draft.image_url || draft.logo_url && (
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <ImagePlus className="text-white" size={24} />
                </div>
              )}
            </label>
            <span className="text-xs text-gray-500">คลิกเพื่ออัปโหลดโลโก้</span>
          </div>
          <label className="text-sm">ชื่อหลักสูตร <span className="text-red-500 font-bold">*</span></label>
          <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
          <label className="text-sm">ชื่อย่อชื่อหลักสูตร <span className="text-red-500 font-bold">*</span></label>
          <input value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />

          <button disabled={saving || !draft.code.trim() || !draft.name.trim()} className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 disabled:opacity-50">{saving ? <Loader size={16} className="animate-spin" /> : "บันทึก"}</button>
        </form>
      </Drawer>

      {usersModal.open && <CourseUsersModal {...usersModal} onClose={() => { setUsersModal({ open: false, courseId: null, courseName: "" }); load(); }} />}
      {testsModal.open && <CourseTestsModal {...testsModal} onClose={() => { setTestsModal({ open: false, courseId: null, courseName: "" }); load(); }} />}
      {contentsModal.open && <CourseContentModal {...contentsModal} onClose={() => { setContentsModal({ open: false, courseId: null, courseName: "" }); load(); }} />}
      {delItem && <ConfirmModal type="confirm" title="ยืนยันการลบ" message={`ลบหลักสูตร "${delItem.name}" ?`} onConfirm={confirmDelete} onCancel={() => setDelItem(null)} loading={deleting} />}
      {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: "" })} />}
    </motion.section>
  )
}
